
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

export function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <section className="relative min-h-screen w-full overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/9acf540dec95fde23784f4db934ca461/255699a461dc084605a46bc09879a80c.png"
          alt="우미린 에듀하이 전경"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      </div>

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img
            src="https://static.readdy.ai/image/9acf540dec95fde23784f4db934ca461/cfecc50567285b9240dacf462e199b6e.png"
            alt="우미린 로고"
            className="h-8 sm:h-10 md:h-12"
          />
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <button
              onClick={() => scrollToSection('awards')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-amber-600' : 'text-white hover:text-amber-400'
              }`}
            >
              브랜드 수상
            </button>
            <button
              onClick={() => scrollToSection('location')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-amber-600' : 'text-white hover:text-amber-400'
              }`}
            >
              입지 프리미엄
            </button>
            <button
              onClick={() => scrollToSection('floorplan')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-amber-600' : 'text-white hover:text-amber-400'
              }`}
            >
              평면 정보
            </button>
            <button
              onClick={() => scrollToSection('amenities')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-amber-600' : 'text-white hover:text-amber-400'
              }`}
            >
              커뮤니티
            </button>
            <button
              onClick={() => scrollToSection('consultation')}
              className="px-5 py-2 sm:px-6 sm:py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-full hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              분양 상담
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              scrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden bg-white shadow-lg border-t"
          >
            <div className="px-4 py-4 space-y-2">
              <button
                onClick={() => scrollToSection('awards')}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 rounded-lg font-medium cursor-pointer"
              >
                브랜드 수상
              </button>
              <button
                onClick={() => scrollToSection('location')}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 rounded-lg font-medium cursor-pointer"
              >
                입지 프리미엄
              </button>
              <button
                onClick={() => scrollToSection('floorplan')}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 rounded-lg font-medium cursor-pointer"
              >
                평면 정보
              </button>
              <button
                onClick={() => scrollToSection('amenities')}
                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-amber-50 rounded-lg font-medium cursor-pointer"
              >
                커뮤니티
              </button>
              <button
                onClick={() => scrollToSection('consultation')}
                className="w-full mt-2 px-4 py-3 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors cursor-pointer"
              >
                분양 상담
              </button>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Hero Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center pt-20 sm:pt-24 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="flex items-center justify-center">
            {/* Center: Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-center px-4"
            >
              <p className="text-amber-400 text-base sm:text-lg font-light mb-3 sm:mb-4 tracking-wide">
                화성 남양뉴타운
              </p>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-3 sm:mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                우미린 에듀하이
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 sm:mb-10">
                556세대 프리미엄 84㎡(34평) 단일 타입
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button
                  onClick={() => scrollToSection('consultation')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-amber-600 text-white font-semibold rounded-full hover:bg-amber-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  분양 상담 신청
                </button>
                <button
                  onClick={() => scrollToSection('overview')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-transparent text-white font-semibold rounded-full border-2 border-white hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  단지 정보 보기
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 z-10 hidden sm:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2 cursor-pointer"
          onClick={() => scrollToSection('awards')}
        >
          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
}
